import React, { useState, useEffect } from 'react';
import { Badge, Tooltip, Button, Drawer, Typography, Space, Divider, theme } from 'antd';
import { 
  WifiOutlined, 
  DisconnectOutlined, 
  CloudSyncOutlined, 
  ReloadOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useOfflineDetection } from '../hooks/useOfflineDetection';
import { getUnsyncedSubmissions } from '../utils/offlineStorage';
import { syncOfflineSubmissions, getSyncStatus } from '../services/syncService';

const { Text, Title } = Typography;

const OfflineStatusIndicator = ({ compact = false }) => {
  const { token } = theme.useToken();
  const { 
    isOnline, 
    connectionType, 
    connectionSpeed, 
    lastOfflineTime, 
    offlineDuration,
    connectionInfo 
  } = useOfflineDetection();
  
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  // Update unsynced count periodically
  useEffect(() => {
    const updateUnsyncedCount = async () => {
      try {
        const submissions = await getUnsyncedSubmissions();
        setUnsyncedCount(submissions.length);
      } catch (error) {
        console.error('[OfflineStatusIndicator] Failed to get unsynced count:', error);
      }
    };

    updateUnsyncedCount();
    const interval = setInterval(updateUnsyncedCount, 5000);

    return () => clearInterval(interval);
  }, []);

  // Update sync status
  useEffect(() => {
    const updateSyncStatus = () => {
      setSyncStatus(getSyncStatus());
    };

    updateSyncStatus();
    const interval = setInterval(updateSyncStatus, 2000);

    return () => clearInterval(interval);
  }, []);

  // Handle manual sync
  const handleManualSync = async () => {
    if (isOnline) {
      try {
        const result = await syncOfflineSubmissions(true);
        if (result.success) {
          // Update count after successful sync
          const submissions = await getUnsyncedSubmissions();
          setUnsyncedCount(submissions.length);
        }
      } catch (error) {
        console.error('[OfflineStatusIndicator] Manual sync failed:', error);
      }
    }
  };

  // Get connection quality indicator
  const getConnectionQuality = () => {
    if (!isOnline) return 'offline';
    
    const speed = connectionInfo.effectiveType;
    if (speed === '4g') return 'excellent';
    if (speed === '3g') return 'good';
    if (speed === '2g') return 'fair';
    if (speed === 'slow-2g') return 'poor';
    return 'unknown';
  };

  // Get status color
  const getStatusColor = () => {
    if (!isOnline) return '#ff4d4f';
    if (syncStatus?.inProgress) return '#faad14';
    if (unsyncedCount > 0) return '#1890ff';
    return '#52c41a';
  };

  // Get status text
  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (syncStatus?.inProgress) return 'Syncing...';
    if (unsyncedCount > 0) return `${unsyncedCount} pending`;
    return 'Online';
  };

  // Compact view for mobile
  if (compact) {
    return (
      <div style={{
        position: 'fixed',
        top: 80,
        right: 12,
        zIndex: 999,
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <Badge 
          count={unsyncedCount > 0 ? unsyncedCount : 0}
          size="small"
          style={{ backgroundColor: getStatusColor() }}
        >
          <Button
            type="text"
            icon={isOnline ? <WifiOutlined /> : <DisconnectOutlined />}
            style={{
              color: getStatusColor(),
              border: `1px solid ${getStatusColor()}`,
              borderRadius: 20,
              fontSize: 12,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={() => setDetailsVisible(true)}
          />
        </Badge>
      </div>
    );
  }

  // Full view for desktop
  return (
    <>
      <div style={{
        position: 'fixed',
        top: 90,
        right: 20,
        zIndex: 999,
        backgroundColor: token.colorBgContainer,
        borderRadius: 12,
        padding: 12,
        boxShadow: token.boxShadowTertiary,
        border: `1px solid ${getStatusColor()}`,
        minWidth: 200,
        backdropFilter: 'blur(10px)',
        opacity: 0.95
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Badge 
            count={unsyncedCount > 0 ? unsyncedCount : 0}
            size="small"
            style={{ backgroundColor: getStatusColor() }}
          >
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: getStatusColor(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {isOnline ? (
                <WifiOutlined style={{ color: 'white', fontSize: 8 }} />
              ) : (
                <DisconnectOutlined style={{ color: 'white', fontSize: 8 }} />
              )}
            </div>
          </Badge>
          
          <Text strong style={{ color: getStatusColor() }}>
            {getStatusText()}
          </Text>
        </div>

        {syncStatus?.inProgress && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CloudSyncOutlined spin style={{ color: '#faad14', fontSize: 12 }} />
            <Text style={{ fontSize: 12, color: '#faad14' }}>Syncing...</Text>
          </div>
        )}

        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          onClick={handleManualSync}
          disabled={!isOnline || syncStatus?.inProgress}
          style={{ fontSize: 12, height: 24, padding: '0 8px' }}
        >
          Sync Now
        </Button>
      </div>

      {/* Details Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <DatabaseOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            <span>Connection Status</span>
          </div>
        }
        placement="right"
        onClose={() => setDetailsVisible(false)}
        open={detailsVisible}
        width={350}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Connection Status */}
          <div>
            <Title level={5} style={{ marginBottom: 12 }}>Connection Status</Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              {isOnline ? (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
              ) : (
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
              )}
              <Text strong>
                {isOnline ? 'Connected' : 'Disconnected'}
              </Text>
            </div>
            
            {lastOfflineTime && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                <Text type="secondary">
                  Last offline: {new Date(lastOfflineTime).toLocaleString()}
                </Text>
              </div>
            )}
            
            {offlineDuration > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                <Text type="secondary">
                  Offline duration: {offlineDuration} seconds
                </Text>
              </div>
            )}
          </div>

          <Divider />

          {/* Connection Details */}
          <div>
            <Title level={5} style={{ marginBottom: 12 }}>Connection Details</Title>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Type:</Text>
                <Text strong>{connectionType}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Speed:</Text>
                <Text strong>{connectionSpeed}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Quality:</Text>
                <Text strong>{getConnectionQuality()}</Text>
              </div>
              {connectionInfo.downlink !== 'unknown' && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">Downlink:</Text>
                  <Text strong>{connectionInfo.downlink} Mbps</Text>
                </div>
              )}
            </Space>
          </div>

          <Divider />

          {/* Sync Status */}
          <div>
            <Title level={5} style={{ marginBottom: 12 }}>Data Sync</Title>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text type="secondary">Pending submissions:</Text>
              <Text strong style={{ color: unsyncedCount > 0 ? '#ff4d4f' : '#52c41a' }}>
                {unsyncedCount}
              </Text>
            </div>
            
            {syncStatus?.lastSyncTime && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text type="secondary">Last sync:</Text>
                <Text>{new Date(syncStatus.lastSyncTime).toLocaleString()}</Text>
              </div>
            )}
            
            {syncStatus?.retryCount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text type="secondary">Retrying:</Text>
                <Text style={{ color: '#faad14' }}>{syncStatus.retryCount} submissions</Text>
              </div>
            )}

            <Button
              type="primary"
              icon={<CloudSyncOutlined />}
              onClick={handleManualSync}
              disabled={!isOnline || syncStatus?.inProgress}
              loading={syncStatus?.inProgress}
              block
              style={{ marginTop: 12 }}
            >
              {syncStatus?.inProgress ? 'Syncing...' : 'Sync Now'}
            </Button>
          </div>
        </Space>
      </Drawer>
    </>
  );
};

export default OfflineStatusIndicator;